import { Match, Option } from 'effect';
import type { CloudStorageProvider } from '@listening/api-contract/integration-providers';
import type { AudioItemSummary } from '@listening/api-contract/legacy-client-types';
import { formatBytes } from '@/lib/format-bytes';
import * as m from '@/paraglide/messages';
import { getLocale } from '@/paraglide/runtime';

const providerName = (provider: CloudStorageProvider) =>
  provider === 'dropbox' ? m.dropbox_title() : m.google_drive_title();

const languageName = (code: string) =>
  Option.liftThrowable((language: string) =>
    new Intl.DisplayNames([getLocale()], { type: 'language' }).of(language),
  )(code).pipe(
    Option.flatMap(Option.fromNullishOr),
    Option.getOrElse(() => code),
  );

const formatCount = (count: number) => count.toLocaleString(getLocale());

export function conversionErrorMessage({
  error,
  error_detail,
}: Pick<AudioItemSummary, 'error' | 'error_detail'>) {
  if (error_detail === null) return error ?? m.conversion_error_generic();
  return Match.valueTags(error_detail, {
    Generic: () => m.conversion_error_generic(),
    DispatchFailed: () => m.conversion_error_dispatch_failed(),
    NoReadableText: () => m.conversion_error_no_readable_text(),
    DrmProtected: () => m.conversion_error_drm_protected(),
    TooManyPages: (failure) =>
      m.conversion_error_too_many_pages({
        pageCount: formatCount(failure.page_count),
        maxPages: formatCount(failure.max_pages),
      }),
    UnsupportedLanguage: (failure) =>
      failure.language === 'und'
        ? m.conversion_error_unsupported_language_unknown()
        : m.conversion_error_unsupported_language({
            language: languageName(failure.language),
          }),
    WebPageEmpty: () => m.conversion_error_web_page_empty(),
    WebPageForbidden: () => m.conversion_error_web_page_forbidden(),
    WebPageBlocked: () => m.conversion_error_web_page_blocked(),
    WebPageUnreachable: () => m.conversion_error_web_page_unreachable(),
    PdfInvalid: () => m.conversion_error_pdf_invalid(),
    PdfPasswordProtected: () => m.conversion_error_pdf_password_protected(),
    CostExceeded: () => m.conversion_error_cost_exceeded(),
    TooManyAttempts: () => m.conversion_error_too_many_attempts(),
    DocumentTooLarge: (failure) =>
      m.conversion_error_document_too_large({
        size: formatBytes(failure.size_bytes),
        maxSize: formatBytes(failure.max_bytes),
      }),
    DocumentTypeUnsupported: () =>
      m.conversion_error_document_type_unsupported(),
    SourceReconnectRequired: (failure) =>
      m.conversion_error_source_reconnect_required({
        provider: providerName(failure.source),
      }),
    SourceFileUnavailable: (failure) =>
      m.conversion_error_source_file_unavailable({
        provider: providerName(failure.source),
      }),
    SourceUnavailable: (failure) =>
      m.conversion_error_source_unavailable({
        provider: providerName(failure.source),
      }),
  });
}
